import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LeadForm } from "@/components/LeadForm";
import { LeadDetail } from "@/components/LeadDetail";
import { BulkActionBar } from "@/components/BulkActionBar";
import { LeadsTableSkeleton } from "@/components/LeadsTableSkeleton";
import { MobileLeadCard } from "@/components/MobileLeadCard";
import { ScoreBadge } from "@/components/ScoreBadge";
import { exportLeadsToCSV } from "@/lib/export";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2, Eye, Building2,
  PoundSterling, Users, Download
} from "lucide-react";
import { cn } from "@/lib/utils";

const stageConfig = {
  new: { label: "New", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  contacted: { label: "Contacted", cls: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20" },
  qualified: { label: "Qualified", cls: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  proposal: { label: "Proposal", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  negotiation: { label: "Negotiation", cls: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  won: { label: "Won", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  lost: { label: "Lost", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

const sourceLabels = {
  website: "Website", phone: "Phone", referral: "Referral",
  "walk-in": "Walk-in", email: "Email", trade_show: "Trade Show", social_media: "Social Media"
};

const EmptyState = ({ onAdd }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-4">
    <div className="w-14 h-14 rounded-sm bg-white/[0.04] border border-white/5 flex items-center justify-center">
      <Users className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
    </div>
    <div className="text-center">
      <p className="text-base font-semibold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        No leads yet
      </p>
      <p className="text-sm text-muted-foreground mt-1">
        Start building your pipeline by adding your first lead
      </p>
    </div>
    <Button
      data-testid="leads-empty-add"
      onClick={onAdd}
      className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-9 px-5 mt-1"
    >
      <Plus className="w-4 h-4 mr-2" /> Add Lead
    </Button>
  </div>
);

const MobileSkeletonList = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="p-4 rounded-sm border border-white/5 bg-card space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-sm flex-shrink-0" />
          <div className="space-y-1.5 flex-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-5 w-14 rounded-sm" />
          <Skeleton className="h-5 w-20 rounded-sm" />
          <Skeleton className="h-5 w-12 rounded-sm ml-auto" />
        </div>
      </div>
    ))}
  </div>
);

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (stageFilter !== "all") params.stage = stageFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      const res = await api.get('/leads', { params });
      setLeads(res.data);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter, sourceFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleCreate = async (data) => {
    try {
      await api.post('/leads', data);
      toast.success("Lead created");
      setShowForm(false);
      fetchLeads();
    } catch (err) {
      toast.error("Failed to create lead");
    }
  };

  const handleEdit = async (data) => {
    try {
      await api.put(`/leads/${editLead.id}`, data);
      toast.success("Lead updated");
      setEditLead(null);
      fetchLeads();
    } catch (err) {
      toast.error("Failed to update lead");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (err) {
      toast.error("Failed to delete lead");
    }
  };

  const allSelected = leads.length > 0 && leads.every(l => selectedIds.has(l.id));
  const someSelected = leads.some(l => selectedIds.has(l.id));

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(leads.map(l => l.id)));
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkStageChange = async (stage) => {
    try {
      await api.patch('/leads/bulk/stage', { lead_ids: [...selectedIds], stage });
      toast.success(`Stage updated for ${selectedIds.size} lead${selectedIds.size !== 1 ? "s" : ""}`);
      setSelectedIds(new Set());
      fetchLeads();
    } catch (err) {
      toast.error("Failed to update stage");
    }
  };

  const handleBulkDelete = async () => {
    try {
      await api.delete('/leads/bulk', { data: { lead_ids: [...selectedIds] } });
      toast.success(`${selectedIds.size} lead${selectedIds.size !== 1 ? "s" : ""} deleted`);
      setSelectedIds(new Set());
      fetchLeads();
    } catch (err) {
      toast.error("Failed to delete leads");
    }
  };

  return (
    <div data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Leads
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">
            Manage all your commercial gas engineering leads
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="leads-export-csv"
            variant="outline"
            onClick={() => exportLeadsToCSV(leads)}
            className="rounded-sm border-white/10 uppercase tracking-wider text-xs h-10 px-4"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            data-testid="leads-add-lead"
            onClick={() => setShowForm(true)}
            className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-10 px-5"
          >
            <Plus className="w-4 h-4 mr-2" /> New Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-0 sm:min-w-[220px] sm:max-w-[360px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="leads-search"
            placeholder="Search leads..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-10 bg-secondary/50 border-white/5 rounded-sm"
          />
        </div>
        <div className="flex gap-3">
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger data-testid="leads-stage-filter" className="w-full sm:w-[160px] h-10 bg-secondary/50 border-white/5 rounded-sm">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {Object.entries(stageConfig).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger data-testid="leads-source-filter" className="w-full sm:w-[160px] h-10 bg-secondary/50 border-white/5 rounded-sm">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {Object.entries(sourceLabels).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Desktop Table */}
      <Card className="border-white/5 bg-card rounded-sm hidden md:block" data-testid="leads-table-card">
        {loading ? (
          <LeadsTableSkeleton />
        ) : leads.length === 0 ? (
          <EmptyState onAdd={() => setShowForm(true)} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="w-10 pl-4">
                  <Checkbox
                    checked={allSelected}
                    data-state={someSelected && !allSelected ? "indeterminate" : undefined}
                    onCheckedChange={toggleSelectAll}
                    data-testid="leads-select-all"
                    className="border-white/20"
                    aria-label="Select all leads"
                  />
                </TableHead>
                <TableHead className="text-xs font-bold tracking-widest uppercase">Company</TableHead>
                <TableHead className="text-xs font-bold tracking-widest uppercase">Contact</TableHead>
                <TableHead className="text-xs font-bold tracking-widest uppercase">Service</TableHead>
                <TableHead className="text-xs font-bold tracking-widest uppercase">Source</TableHead>
                <TableHead className="text-xs font-bold tracking-widest uppercase">Stage</TableHead>
                <TableHead className="text-xs font-bold tracking-widest uppercase text-right">Score</TableHead>
                <TableHead className="text-xs font-bold tracking-widest uppercase text-right">Value</TableHead>
                <TableHead className="text-xs font-bold tracking-widest uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead, i) => (
                <TableRow
                  key={lead.id}
                  data-testid={`lead-row-${lead.id}`}
                  className={cn(
                    "border-white/5 hover:bg-white/[0.03] cursor-pointer animate-fade-slide-up",
                    selectedIds.has(lead.id) && "bg-blue-500/5"
                  )}
                  style={{ animationDelay: `${i * 30}ms` }}
                  onClick={() => setDetailLead(lead)}
                >
                  <TableCell className="pl-4" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(lead.id)}
                      onCheckedChange={() => toggleSelectOne(lead.id)}
                      data-testid={`lead-checkbox-${lead.id}`}
                      className="border-white/20"
                      aria-label={`Select ${lead.company_name}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-sm bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-3.5 h-3.5 text-blue-400" strokeWidth={1.5} />
                      </div>
                      <span className="font-medium text-sm">{lead.company_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{lead.contact_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{lead.service_type}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{sourceLabels[lead.lead_source] || lead.lead_source}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("text-[10px] rounded-sm", stageConfig[lead.stage]?.cls)}>
                      {stageConfig[lead.stage]?.label || lead.stage}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {lead.score != null && <ScoreBadge score={lead.score} />}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <PoundSterling className="w-3 h-3 text-amber-400" />
                      <span className="text-sm font-semibold text-amber-400">{Number(lead.estimated_value).toLocaleString()}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`lead-actions-${lead.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-card border-white/10">
                        <DropdownMenuItem onClick={() => setDetailLead(lead)}><Eye className="w-4 h-4 mr-2" /> View</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditLead(lead)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDelete(lead.id)} className="text-red-400 focus:text-red-400" data-testid={`lead-delete-${lead.id}`}>
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Mobile Card List */}
      <div className="md:hidden space-y-3" data-testid="leads-mobile-list">
        {loading ? (
          <MobileSkeletonList />
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-14 h-14 rounded-sm bg-white/[0.04] border border-white/5 flex items-center justify-center">
              <Users className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <div className="text-center">
              <p className="text-base font-semibold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>No leads yet</p>
              <p className="text-sm text-muted-foreground mt-1">Start building your pipeline by adding your first lead</p>
            </div>
            <Button
              data-testid="leads-empty-add-mobile"
              onClick={() => setShowForm(true)}
              className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-9 px-5"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Lead
            </Button>
          </div>
        ) : (
          leads.map(lead => (
            <MobileLeadCard
              key={lead.id}
              lead={lead}
              onView={l => setDetailLead(l)}
              onEdit={l => setEditLead(l)}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      <div className="mt-4 text-xs text-muted-foreground">
        Showing {leads.length} lead{leads.length !== 1 ? "s" : ""}
      </div>

      <LeadForm open={showForm} onOpenChange={setShowForm} onSubmit={handleCreate} />
      <LeadForm open={!!editLead} onOpenChange={(v) => !v && setEditLead(null)} onSubmit={handleEdit} initialData={editLead} />
      <LeadDetail lead={detailLead} open={!!detailLead} onOpenChange={(v) => !v && setDetailLead(null)} onEdit={(lead) => { setDetailLead(null); setEditLead(lead); }} onRefresh={fetchLeads} />

      <BulkActionBar
        selectedCount={selectedIds.size}
        onStageChange={handleBulkStageChange}
        onDelete={handleBulkDelete}
        onClear={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
